import { RefObject } from "react";
import type { CoordinateSet } from "@/map/types/map";
import { calculateMapBounds } from "@/map/utils/mapCalculations";
import { ZOOM_LIMITS } from "@/map/utils/mapConstants";

interface UseMapInitProps {
  mapRef: RefObject<google.maps.Map | null>;
  mapsRef: RefObject<typeof google.maps | null>;
  allCoordinates: CoordinateSet[];
  initialHeight: number;
  onZoomChanged: (map: google.maps.Map) => void;
  onBoundsChanged: (map: google.maps.Map) => void;
}

export const useMapInit = ({
  mapRef,
  mapsRef,
  allCoordinates,
  initialHeight,
  onZoomChanged,
  onBoundsChanged,
}: UseMapInitProps) => {
  /**
   * Initialize map when Google Maps API is loaded
   */
  const handleGoogleApiLoaded = ({
    map,
    maps,
  }: {
    map: google.maps.Map;
    maps: typeof google.maps;
  }) => {
    mapRef.current = map;
    mapsRef.current = maps;

    // Add zoom listener
    map.addListener("zoom_changed", () => {
      onZoomChanged(map);
    });

    // Add bounds listener
    map.addListener("bounds_changed", () => {
      onBoundsChanged(map);
    });

    // Fit map to show all markers
    const bounds = calculateMapBounds(allCoordinates);
    if (bounds) {
      const isSmallMap = initialHeight <= 300;

      if (isSmallMap) {
        google.maps.event.addListenerOnce(map, "idle", () => {
          const center = bounds.getCenter();
          map.setCenter(center);

          const totalPoints = allCoordinates.flatMap((coord) => coord.path).length;
          if (totalPoints === 1) {
            map.setZoom(ZOOM_LIMITS.SINGLE_POINT);
          } else {
            map.setZoom(ZOOM_LIMITS.SMALL_MAP);
          }
        });
      } else {
        map.fitBounds(bounds);
        google.maps.event.addListenerOnce(map, "bounds_changed", () => {
          const currentZoom = map.getZoom();
          if (currentZoom && currentZoom > ZOOM_LIMITS.MAX_ZOOM) {
            map.setZoom(ZOOM_LIMITS.MAX_ZOOM);
          }
        });
      }
    } else {
      // No bounds, set default zoom
      const isSmallMap = initialHeight <= 300;
      map.setZoom(isSmallMap ? ZOOM_LIMITS.SMALL_MAP_DEFAULT : ZOOM_LIMITS.DEFAULT);
    }
  };

  return {
    handleGoogleApiLoaded,
  };
};
